import { useMemo } from "react";
import { useAuth } from "../../../auth";
import { getSelectedIntegrations, needsOnboarding as computeNeedsOnboarding } from "./org-onboarding-helpers";
import type { IntegrationName, OrgOnboardingStatus } from "./types";

/**
 * Reads the selected organization's `onboardingStatus` record, with derived flags.
 *
 * @returns
 *   - `status`: the {@link OrgOnboardingStatus} record (`{}` when unset).
 *   - `isLoading`: `true` while organizations are still loading.
 *   - `needsOnboarding`: `true` when the org is on a recognized onboarding plan.
 *   - `selectedIntegrations`: integrations present on the record, in canonical order.
 */
export const useOrgOnboardingStatus = (): {
  status: OrgOnboardingStatus;
  isLoading: boolean;
  needsOnboarding: boolean;
  selectedIntegrations: IntegrationName[];
} => {
  const { selectedOrganization, isLoadingOrganizations } = useAuth();

  return useMemo(() => {
    return {
      status: selectedOrganization?.onboardingStatus,
      isLoading: isLoadingOrganizations,
      needsOnboarding: computeNeedsOnboarding(selectedOrganization?.plan?.type),
      selectedIntegrations: getSelectedIntegrations(selectedOrganization?.onboardingStatus),
    };
  }, [selectedOrganization?.onboardingStatus, selectedOrganization?.plan?.type, isLoadingOrganizations]);
};
